"use client"

/**
 * Shared "Regenerate token" control used by `web_widget` and `web_demo`
 * settings. Wraps an AlertDialog around a Button so every settings panel
 * gets the same confirm-before-invalidate UX without duplicating the
 * dialog markup.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Loader2, RefreshCw } from "lucide-react"
import { useTranslations } from "next-intl"


interface RegenerateTokenButtonProps {
  onConfirm: () => void
  loading: boolean
  label?: string
  dialogTitle?: string
  dialogDescription?: string
  confirmLabel?: string
  variant?: "ghost" | "outline"
}

export function RegenerateTokenButton({
  onConfirm,
  loading,
  label = "Regenerate embed code",
  dialogTitle = "Regenerate embed code?",
  dialogDescription = "This will generate a new embed code and immediately invalidate the old one. All existing widget embeds will stop working until you update the code on your website.",
  confirmLabel = "Regenerate",
  variant = "ghost",
}: RegenerateTokenButtonProps) {
  const tCommonUi = useTranslations("Common")

  const Icon = loading ? Loader2 : RefreshCw
  const iconClass = loading ? "w-3.5 h-3.5 mr-2 animate-spin" : "w-3.5 h-3.5 mr-2"

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant={variant}
          size="sm"
          className={variant === "ghost" ? "text-gray-500" : undefined}
          disabled={loading}
        >
          <Icon className={iconClass} />
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{dialogTitle}</AlertDialogTitle>
          <AlertDialogDescription>{dialogDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
